import { useCallback, useEffect, useState } from 'react';
import { isValidPaymentSelection } from '../../../types/schedule.types';
import type { PaymentSelection } from '../../../types/schedule.types';
import type { Product } from '@/features/products/types/products.types';
import type { ConfiguredService, ConfiguredAddOn } from '@/features/service-plans/types/services.types';
import { resolveDefaultMethodKey } from './derive';
import { seedDepositMinor } from './depositMode';
import type { DepositOrigin, ProductDeposit } from './depositMode';

interface DepositState {
    amountMinor: number | undefined;
    origin: DepositOrigin;
    discards: number;
    seededFor: string | undefined;
}

export interface PaymentSelectionResumeResult {
    selectedMethodKey: string | null;
    setSelectedMethodKey: (key: string | null) => void;
    depositMinor: number | undefined;
    depositOrigin: DepositOrigin;
    depositDiscards: number;
    onDepositInput: (value: number | undefined) => void;
    onDepositEdit: () => void;
    writeScheduleDeposit: (amountMinor: number) => void;
    discardProductDeposit: (judgedMinor: number) => void;
    leadOverrideMonths: number | undefined;
    setLeadOverrideMonths: (value: number | undefined) => void;
    collectionInstalments: number | undefined;
    setCollectionInstalments: (value: number | undefined) => void;
}

/** The local state PaymentStep seeds from a resumed selection, or from defaults for a
 *  fresh one, plus the effect that reseeds it once per quote. */
export function usePaymentSelectionResume(
    quoteId: string | undefined,
    paymentSelection: PaymentSelection | undefined,
    selectedProduct: Product | null | undefined,
    configuredServices: ConfiguredService[],
    configuredAddOns: ConfiguredAddOn[],
    productDeposit: ProductDeposit | undefined,
): PaymentSelectionResumeResult {
    const [selectedMethodKey, setSelectedMethodKey] = useState<string | null>(
        () =>
            paymentSelection?.methodKey ??
            resolveDefaultMethodKey(selectedProduct, configuredServices, configuredAddOns),
    );

    const [deposit, setDeposit] = useState<DepositState>(() => ({
        ...seedDepositMinor(paymentSelection, productDeposit),
        discards: 0,
        seededFor: quoteId,
    }));

    const [leadOverrideMonths, setLeadOverrideMonths] = useState<number | undefined>(
        isValidPaymentSelection(paymentSelection) ? paymentSelection.collectionLeadMonths : undefined,
    );

    // 2.4.7: the resumed Number-of-Collections choice. The even-monthly count lives in
    // `params.numInstalments`; an out-of-range stale value is ignored by `resolveChosenInstalments`.
    const [collectionInstalments, setCollectionInstalments] = useState<number | undefined>(
        isValidPaymentSelection(paymentSelection) ? paymentSelection.params.numInstalments : undefined,
    );

    useEffect(() => {
        setSelectedMethodKey(
            paymentSelection?.methodKey ??
                resolveDefaultMethodKey(selectedProduct, configuredServices, configuredAddOns),
        );
        setDeposit((current) => {
            const seeded = seedDepositMinor(paymentSelection, productDeposit);
            return seeded.amountMinor === current.amountMinor &&
                seeded.origin === current.origin &&
                current.seededFor === quoteId
                ? current
                : { ...seeded, discards: current.discards, seededFor: quoteId };
        });
        setLeadOverrideMonths(
            isValidPaymentSelection(paymentSelection) ? paymentSelection.collectionLeadMonths : undefined,
        );
        setCollectionInstalments(
            isValidPaymentSelection(paymentSelection) ? paymentSelection.params.numInstalments : undefined,
        );
        // Keyed on quoteId only so a loaded quote resets local selection once
        // without a re-render loop.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [quoteId]);

    const productDepositMinor = productDeposit?.amountMinor;

    useEffect(() => {
        setDeposit((current) => {
            if (current.origin === 'schedule') {
                return productDepositMinor !== undefined
                    ? { ...current, amountMinor: productDepositMinor, origin: 'product' }
                    : { ...current, amountMinor: undefined, origin: 'blank' };
            }
            return current.origin === 'blank' && productDepositMinor !== undefined
                ? { ...current, amountMinor: productDepositMinor, origin: 'product' }
                : current;
        });
    }, [selectedMethodKey, productDepositMinor]);

    const onDepositInput = useCallback((value: number | undefined) => {
        setDeposit((current) =>
            value === current.amountMinor
                ? current
                : { ...current, amountMinor: value, origin: value === undefined ? 'cleared' : 'entered' },
        );
    }, []);

    const onDepositEdit = useCallback(() => {
        setDeposit((current) =>
            current.origin === 'product' || current.origin === 'blank' || current.origin === 'schedule'
                ? { ...current, origin: 'entered' }
                : current,
        );
    }, []);

    const writeScheduleDeposit = useCallback((amountMinor: number) => {
        setDeposit((current) =>
            current.origin === 'blank' ? { ...current, amountMinor, origin: 'schedule' } : current,
        );
    }, []);

    // Bound to the quote the judging render held, so a judgement from before the reseed never lands.
    const judgedFor = deposit.seededFor;
    const discardProductDeposit = useCallback(
        (judgedMinor: number) => {
            setDeposit((current) =>
                current.seededFor === judgedFor && current.origin === 'product' && current.amountMinor === judgedMinor
                    ? { ...current, amountMinor: undefined, origin: 'blank', discards: current.discards + 1 }
                    : current,
            );
        },
        [judgedFor],
    );

    return {
        selectedMethodKey,
        setSelectedMethodKey,
        depositMinor: deposit.amountMinor,
        depositOrigin: deposit.origin,
        depositDiscards: deposit.discards,
        onDepositInput,
        onDepositEdit,
        writeScheduleDeposit,
        discardProductDeposit,
        leadOverrideMonths,
        setLeadOverrideMonths,
        collectionInstalments,
        setCollectionInstalments,
    };
}
